import { Routes } from '@angular/router';
import { authGuard, roleGuard } from './guards/role.guard';

// Routes reachable without an authenticated/role-checked session.
const publicRoutes: Routes = [
    { path: '', redirectTo: '/Login', pathMatch: 'full' },
    {
        path: 'Login',
        loadComponent: () => import('./login/login.component').then(m => m.LoginComponent)
    },
    {
        path: 'Auth',
        loadComponent: () => import('./auth/auth.component').then(m => m.AuthComponent),
    },
    {
        path: 'SSO',
        loadComponent: () => import('./sso/sso.component').then(m => m.SSOComponent),
    },
     {
        path: 'ChangePassword',
        loadComponent: () => import('./pages/change-password/change-password.component').then(m => m.ChangePasswordComponent),
        canActivate: [authGuard],
    },
];

// Every other route requires a session and is role-checked against
// src/data/ValidRouts.ts by roleGuard (ADR-032) — applied below by
// construction so a new route added here is protected by default rather
// than needing canActivate remembered on every entry.
const protectedRoutes: Routes = [
    {
        path: 'ActMaster',
        loadComponent: () => import('./pages/master-setup/act-master/act-master.component').then(m => m.ActMasterComponent),
    },
    {
        path: 'ComplianceMaster',
        loadComponent: () => import('./pages/master-setup/compliance-master/compliance-master.component').then(m => m.ComplianceMasterComponent),
    },
    {
        path: 'ComplianceMasterAddEdit',
        loadComponent: () => import('./pages/popups/compliance-master-add-edit/compliance-master-add-edit.component').then(m => m.ComplianceMasterAddEditComponent),
    },
    {
        path: 'CityMaster',
        loadComponent: () => import('./pages/master-setup/city-master/city-master.component').then(m => m.CityMasterComponent),
    },
    {
        path: 'LicenseType',
        loadComponent: () => import('./pages/master-setup/license-type/license-type.component').then(m => m.LicenseTypeComponent),
    },
    {
        path: 'DataPoint',
        loadComponent: () => import('./pages/master-setup/data-points/data-points.component').then(m => m.DataPointsComponent),
    },
    {
        path: 'Customer',
        loadComponent: () => import('./pages/onboard-entity/customer/customer.component').then(m => m.CustomerComponent),
    },
    {
        path: 'CustomerAddEdit',
        loadComponent: () => import('./pages/popups/customer-add-edit/customer-add-edit.component').then(m => m.CustomerAddEditComponent),
    },
    {
        path: 'Entity',
        loadComponent: () => import('./pages/onboard-entity/entity/entity.component').then(m => m.EntityComponent),
    },
    {
        path: 'EntityAddEdit',
        loadComponent: () => import('./pages/popups/entity-add-edit/entity-add-edit.component').then(m => m.EntityAddEditComponent),
    },
    {
        path: 'User',
        loadComponent: () => import('./pages/onboard-entity/user/user.component').then(m => m.UserComponent),
    },
    {
        path: 'Premises',
        loadComponent: () => import('./pages/onboard-entity/premises/premises.component').then(m => m.PremisesComponent),
    },
    {
        path: 'PremisesAddEdit',
        loadComponent: () => import('./pages/popups/permises-add-edit/permises-add-edit.component').then(m => m.PermisesAddEditComponent),
    },
    {
        path: 'ProjectSite',
        loadComponent: () => import('./pages/onboard-entity/project-site/project-site.component').then(m => m.ProjectSiteComponent),
    },
    {
        path: 'ProjectSiteAddEdit',
        loadComponent: () => import('./pages/popups/project-site-add-edit/project-site-add-edit.component').then(m => m.ProjectSiteAddEditComponent),
    },
    {
        path: 'Contractors',
        loadComponent: () => import('./pages/contractor/contractors/contractors.component').then(m => m.ContractorsComponent),
    },
    {
        path: 'ProjectContractorMapping',
        loadComponent: () => import('./pages/contractor/project-contractor-mapping/project-contractor-mapping.component').then(m => m.ProjectContractorMappingComponent),
    },
    {
        path: 'ProjectContractorMappingAddEdit',
        loadComponent: () => import('./pages/popups/project-contractor-mapping-add-edit/project-contractor-mapping-add-edit.component').then(m => m.ProjectContractorMappingAddEditComponent),
    },
    {
        path: 'UnlockedDays',
        loadComponent: () => import('./pages/contractor/unlocked-days/unlocked-days.component').then(m => m.UnlockedDaysComponent),
    },
    {
        path: 'ContractorType',
        loadComponent: () => import('./pages/contractor/contractor-type/contractor-type.component').then(m => m.ContractorTypeComponent),
    },
    {
        path: 'ComplianceTemplate',
        loadComponent: () => import('./pages/assign-compliance/compliance-template/compliance-template.component').then(m => m.ComplianceTemplateComponent),
    },
    {
        path: 'ComplianceTemplateAddEdit',
        loadComponent: () => import('./pages/assign-compliance/compliance-template-add-edit/compliance-template-add-edit.component').then(m => m.ComplianceTemplateAddEditComponent),
    },
    {
        path: 'ComplianceMapping',
        loadComponent: () => import('./pages/assign-compliance/compliance-mapping/compliance-mapping.component').then(m => m.ComplianceMappingComponent),
    },
    {
        path: 'ComplianceMappingView',
        loadComponent: () => import('./pages/assign-compliance/compliance-mapping-view/compliance-mapping-view.component').then(m => m.ComplianceMappingViewComponent),
    },
    {
        path: 'AuditSchedule',
        loadComponent: () => import('./pages/assign-compliance/audit-schedule/audit-schedule.component').then(m => m.AuditScheduleComponent),
    },
    {
        path: 'AuditAssignment',
        loadComponent: () => import('./pages/assign-compliance/audit-assignment/audit-assignment.component').then(m => m.AuditAssignmentComponent),
    },
    {
        path: 'ManageCompliances',
        loadComponent: () => import('./pages/assign-compliance/manage-compliances/manage-compliances.component').then(m => m.ManageCompliancesComponent),
    },
    {
        path: 'ContractorWorkspace',
        loadComponent: () => import('./pages/my-workspace/audits/audits.component').then(m => m.AuditsComponent),
    },
    {
        path: 'License',
        loadComponent: () => import('./pages/my-workspace/license/license.component').then(m => m.LicenseComponent),
    },
    {
        path: 'AuditsContractor',
        loadComponent: () => import('./pages/my-workspace/contractor/contractor.component').then(m => m.ContractorComponent),
    },
    {
        path: 'AuditSubContractor',
        loadComponent: () => import('./pages/my-workspace/sub-contractor/sub-contractor.component').then(m => m.SubContractorComponent),
    },
    {
        path: 'MyDocuments',
        loadComponent: () => import('./pages/my-documents/my-documents/my-documents.component').then(m => m.MyDocumentsComponent),
    },
    {
        path: 'CustomerDashboard',
        loadComponent: () => import('./pages/customer-dashboard/customer-dashboard.component').then(m => m.CustomerDashboardComponent),
    },
    {
        path: 'CustomerMaster',
        loadComponent: () => import('./pages/customer-dashboard/customer-dashboard.component').then(m => m.CustomerDashboardComponent),
    },
    {
        path: 'AuditStatus',
        loadComponent: () => import('./pages/reports/reports/reports.component').then(m => m.ReportsComponent),
    },
    {
        path: 'ComplianceRemarks',
        loadComponent: () => import('./pages/reports/reports/reports.component').then(m => m.ReportsComponent),
    },
    {
        path: 'ClosedAuditReport',
        loadComponent: () => import('./pages/reports/reports/reports.component').then(m => m.ReportsComponent),
    },
    {
        path: 'ComplianceByType',
        loadComponent: () => import('./pages/reports/reports/reports.component').then(m => m.ReportsComponent),
    },
    {
        path: 'DelayedUploadReport',
        loadComponent: () => import('./pages/reports/reports/reports.component').then(m => m.ReportsComponent),
    },
    {
        path: 'DetailedSummaryReport',
        loadComponent: () => import('./pages/reports/reports/reports.component').then(m => m.ReportsComponent),
    },
    {
        path: 'ConsolidatedOverview',
        loadComponent: () => import('./pages/reports/reports/reports.component').then(m => m.ReportsComponent),
    },
    {
        path: 'TrendAnalysisByVendor',
        loadComponent: () => import('./pages/reports/reports/reports.component').then(m => m.ReportsComponent),
    },
    {
        path: 'MoreReports',
        loadComponent: () => import('./pages/reports/reports/reports.component').then(m => m.ReportsComponent),
    },
    {
        path: 'Dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
    },
    {
        path: 'DashboardGrid',
        loadComponent: () => import('./pages/dashboard/dashboard-grid/dashboard-grid.component').then(m => m.DashboardGridComponent),
    },
    {
        path: 'ContractorDashboard',
        loadComponent: () => import('./pages/dashboard/contractor-dashboard/contractor-dashboard.component').then(m => m.ContractorDashboardComponent),
    },
    {
        path: 'NationalFestivalMaster',
        loadComponent: () => import('./pages/master-setup/national-festival-master/national-festival-master.component').then(m => m.NationalFestivalMasterComponent),
    },
    {
        path: 'CustomerUserMapping',
        loadComponent: () => import('./pages/customer-user-mapping/customer-user-mapping/customer-user-mapping.component').then(m => m.CustomerUserMappingComponent),
    },
];

// Not role-checked: the error page must render regardless of role/session
// state, and the wildcard exists to catch unmatched paths.
const errorRoutes: Routes = [
    {
        path: 'Error/:code',
        loadComponent: () => import('./pages/ErrorPages/error-page/error-page.component').then(m => m.ErrorPageComponent),
    },
    {
        path: '**',
        redirectTo: '/Error/404.',
    },
];

export const appRoutes: Routes = [
    ...publicRoutes,
    ...protectedRoutes.map(route => ({ ...route, canActivate: [roleGuard] })),
    ...errorRoutes,
];
